import type { TableCraftFunction } from '@carboncopyco/types';
import { Button, Modal, TextBase, TextCaption } from '@carboncopyco/ui';
import classNames from 'classnames';
import { useState } from 'react';
import ParamInput from './ParamInput';

type FunctionModalProps = {
  fn: TableCraftFunction;
  onClose: () => void;
  onSubmit: (params: Record<string, boolean | string>) => void;
};

const FunctionsModal = ({ fn, onClose, onSubmit }: FunctionModalProps) => {
  const defaultParams = fn.params.reduce(
    (acc, param) => ({
      ...acc,
      [param.name]: param.default,
    }),
    {}
  );

  const [values, setValues] = useState(
    defaultParams as Record<string, boolean | string>
  );

  const requiredParams = fn.params
    .filter((param) => param.required)
    .map((param) => param.name);

  const isSubmitDisabled = requiredParams.some((param) => {
    const keys = Object.keys(values);
    const isRequiredParam = keys.includes(param);
    const value = values[param];
    return isRequiredParam && (value === undefined || value === '');
  });

  const handleParamsValueChange = (
    paramName: string,
    value: boolean | string
  ) => {
    setValues((prevState) => ({
      ...prevState,
      [paramName]: value,
    }));
  };

  const handleSubmit = () => {
    onSubmit(values);
    setValues({});
  };

  const formattedValue = (
    param: TableCraftFunction['params'][0],
    value: string | boolean
  ) => {
    // when selecting the "Last Name" the gender can be only set to be "None"
    if (values.func === 'Last Name' && param.name === 'gender') {
      return 'None';
    }
    return value;
  };

  return (
    <Modal
      title={fn.display_name}
      open
      onClose={() => {
        onClose();
        setValues({});
      }}
      containerClass="w-[620px]"
    >
      <div className="flex flex-col">
        <div className="pt-7 pb-6 flex flex-col gap-4">
          <TextBase
            label={fn.help}
            color="text-ds-natural-600"
            newLineBreak
          />
          {fn.params.length > 0 && (
            <div className="p-4.5 bg-ds-natural-100 rounded-md">
              <div
                className={classNames('grid gap-5', {
                  'grid-cols-2': fn.params.length > 1,
                })}
              >
                {fn.params.map((param) => (
                  <div
                    key={param.name}
                    className="flex flex-col gap-2"
                  >
                    <TextCaption
                      label={
                        <>
                          {param.display_name}
                          {param.required && (
                            <span className="pl-0.5 text-ds-danger-400 text-base">
                              *
                            </span>
                          )}
                        </>
                      }
                      color="text-ds-natural-600"
                      newLineBreak
                    />
                    <ParamInput
                      param={param}
                      value={formattedValue(param, values[param.name])}
                      onChange={(value) =>
                        handleParamsValueChange(param.name, value)
                      }
                    />
                    {param.help && (
                      <TextCaption
                        label={param.help}
                        color="text-ds-natural-400"
                        newLineBreak
                      />
                    )}
                  </div>
                ))}
              </div>
              {fn.example && (
                <div className="mt-2">
                  <TextCaption
                    label={fn.example}
                    color="text-ds-natural-400"
                    tagName="p"
                    newLineBreak
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-1">
          <Button
            secondary
            label="Cancel"
            link
            onClick={() => {
              onClose();
              setValues({});
            }}
          />

          <Button
            label="Apply Function"
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </Modal>
  );
};

export default FunctionsModal;
